import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, Package, Calendar } from 'lucide-react';

const COLORS = ['#22c55e', '#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899'];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const Reports = ({ transactions = [], materials = [], materialUsage = [], clients = [] }) => {
  const [period, setPeriod] = useState('month');
  const [reportTab, setReportTab] = useState('transactions');

  // Processar dados de transações por tipo
  const transactionsByType = useMemo(() => {
    const totals = { entrada: 0, saida: 0, venda: 0 };
    transactions.forEach(t => {
      if (totals[t.type] !== undefined) {
        totals[t.type] += t.amount;
      }
    });
    return [
      { name: 'Entradas', value: totals.entrada, color: '#22c55e' },
      { name: 'Saídas', value: totals.saida, color: '#ef4444' },
      { name: 'Vendas', value: totals.venda, color: '#8b5cf6' }
    ].filter(item => item.value > 0);
  }, [transactions]);

  // Processar dados de transações por dia (últimos 30 dias)
  const transactionsByDay = useMemo(() => {
    const last30Days = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayTransactions = transactions.filter(t => {
        const tDate = new Date(t.date).toISOString().split('T')[0];
        return tDate === dateStr;
      });
      
      const entradas = dayTransactions.filter(t => t.type === 'entrada').reduce((sum, t) => sum + t.amount, 0);
      const saidas = dayTransactions.filter(t => t.type === 'saida').reduce((sum, t) => sum + t.amount, 0);
      const vendas = dayTransactions.filter(t => t.type === 'venda').reduce((sum, t) => sum + t.amount, 0);
      
      last30Days.push({
        date: date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' }),
        entradas,
        saidas,
        vendas,
        saldo: entradas + vendas - saidas
      });
    }
    
    return last30Days;
  }, [transactions]);

  // Calcular evolução do saldo
  const balanceEvolution = useMemo(() => {
    let runningBalance = 0;
    return transactionsByDay.map(day => {
      runningBalance += day.saldo;
      return {
        ...day,
        saldoAcumulado: runningBalance
      };
    });
  }, [transactionsByDay]);

  // Processar dados de materiais
  const materialStats = useMemo(() => {
    return materials.map(m => ({
      name: m.name.length > 15 ? m.name.substring(0, 15) + '...' : m.name,
      fullName: m.name,
      preco: m.unit_price,
      unidade: m.unit
    })).sort((a, b) => b.preco - a.preco).slice(0, 10);
  }, [materials]);

  // Processar uso de materiais por fase
  const materialUsageStats = useMemo(() => {
    const usageByMaterial = {};
    materialUsage.forEach(usage => {
      const name = usage.material_name || 'Desconhecido';
      if (!usageByMaterial[name]) {
        usageByMaterial[name] = { name, quantidade: 0, custo: 0 };
      }
      usageByMaterial[name].quantidade += usage.quantity;
      usageByMaterial[name].custo += usage.total_cost || 0;
    });
    return Object.values(usageByMaterial).sort((a, b) => b.custo - a.custo).slice(0, 10);
  }, [materialUsage]);

  // Transações por cliente
  const transactionsByClient = useMemo(() => {
    const clientTotals = {};
    transactions.forEach(t => {
      if (t.client_name) {
        if (!clientTotals[t.client_name]) {
          clientTotals[t.client_name] = { name: t.client_name, total: 0, count: 0 };
        }
        clientTotals[t.client_name].total += t.amount;
        clientTotals[t.client_name].count += 1;
      }
    });
    return Object.values(clientTotals).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [transactions]);

  // Totais gerais
  const totals = useMemo(() => {
    const entrada = transactions.filter(t => t.type === 'entrada').reduce((sum, t) => sum + t.amount, 0);
    const saida = transactions.filter(t => t.type === 'saida').reduce((sum, t) => sum + t.amount, 0);
    const venda = transactions.filter(t => t.type === 'venda').reduce((sum, t) => sum + t.amount, 0);
    return {
      entrada,
      saida,
      venda,
      saldo: entrada + venda - saida,
      totalTransacoes: transactions.length
    };
  }, [transactions]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-medium text-black">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-green-500 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-green-100 text-sm">Total Entradas</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.entrada)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-red-100 text-sm">Total Saídas</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.saida)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-200 rotate-180" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-500 text-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-purple-100 text-sm">Total Vendas</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.venda)}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card className={totals.saldo >= 0 ? "bg-blue-500 text-white" : "bg-orange-500 text-white"}>
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-blue-100 text-sm">Saldo Atual</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.saldo)}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de relatórios */}
      <Tabs value={reportTab} onValueChange={setReportTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Transações
          </TabsTrigger>
          <TabsTrigger value="materials" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Materiais
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <PieChartIcon className="h-4 w-4" />
            Clientes
          </TabsTrigger>
        </TabsList>

        {/* Relatório de Transações */}
        <TabsContent value="transactions" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de Pizza - Distribuição */}
            <Card>
              <CardHeader>
                <CardTitle className="text-black flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Distribuição por Tipo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactionsByType.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={transactionsByType}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {transactionsByType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    Nenhuma transação encontrada
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gráfico de Barras - Por Dia */}
            <Card>
              <CardHeader>
                <CardTitle className="text-black flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Transações por Dia (Últimos 30 dias)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={transactionsByDay.slice(-15)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="entradas" name="Entradas" fill="#22c55e" />
                    <Bar dataKey="saidas" name="Saídas" fill="#ef4444" />
                    <Bar dataKey="vendas" name="Vendas" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de Evolução do Saldo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-black flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Evolução do Saldo (Últimos 30 dias)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={balanceEvolution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="saldoAcumulado" 
                    name="Saldo Acumulado"
                    stroke="#3b82f6" 
                    fill="#93c5fd" 
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Relatório de Materiais */}
        <TabsContent value="materials" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Materiais por Preço */}
            <Card>
              <CardHeader>
                <CardTitle className="text-black flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Materiais por Preço Unitário (Top 10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {materialStats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={materialStats} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="preco" name="Preço" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    Nenhum material cadastrado
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Uso de Materiais */}
            <Card>
              <CardHeader>
                <CardTitle className="text-black flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Custo por Material Utilizado (Top 10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {materialUsageStats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={materialUsageStats} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="custo" name="Custo Total" fill="#06b6d4" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    Nenhum material utilizado
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tabela de Materiais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-black">Lista Completa de Materiais</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 text-black font-medium">Material</th>
                      <th className="text-left p-2 text-black font-medium">Unidade</th>
                      <th className="text-right p-2 text-black font-medium">Preço Unit.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materials.map((m, index) => (
                      <tr key={m.id || index} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-black">{m.name}</td>
                        <td className="p-2 text-black">{m.unit}</td>
                        <td className="p-2 text-right text-black font-medium">{formatCurrency(m.unit_price)}</td>
                      </tr>
                    ))}
                    {materials.length === 0 && (
                      <tr>
                        <td colSpan={3} className="p-4 text-center text-gray-500">
                          Nenhum material cadastrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Relatório de Clientes */}
        <TabsContent value="clients" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clientes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-black flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Top 5 Clientes por Valor
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactionsByClient.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={transactionsByClient}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name.substring(0, 10)}... ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="total"
                      >
                        {transactionsByClient.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    Nenhuma transação com cliente
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tabela de Clientes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-black">Resumo por Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactionsByClient.map((client, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-black">{client.name}</p>
                        <p className="text-sm text-gray-500">{client.count} transações</p>
                      </div>
                      <p className="font-bold text-black">{formatCurrency(client.total)}</p>
                    </div>
                  ))}
                  {transactionsByClient.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      Nenhuma transação com cliente encontrada
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
